import { useCallback, useState, type ReactElement, type ReactNode } from 'react';
import {
  CustomPageArbitraryFilterFieldsContext,
  CustomPageBrandContext,
  CustomPageDataReadyStateContext,
  CustomPageDispatchActionContext,
  CustomPageProxiesClientContext,
  CustomPageSetArbitraryFilterFieldsContext,
  CustomPageSubjectContext,
  CustomPageTerritoryIdContext,
  CustomPageUserContext,
  FetchUserTerritoriesContext,
  type CustomPageArbitraryFilterFields,
  type CustomPageBrand,
  type CustomPageDispatchAction,
  type CustomPageRendererContextValue,
  type CustomPageSubject,
  type CustomPageUser,
  type UserTerritory,
} from './default.js';

type CustomPageRendererContextProviderProps = {
  value: CustomPageRendererContextValue;
  fetchUserTerritories: () => Promise<UserTerritory[]>;
  children: ReactNode;
};

export function CustomPageRendererContextProvider({
  value,
  fetchUserTerritories,
  children,
}: CustomPageRendererContextProviderProps): ReactElement {
  return (
    <CustomPageDataReadyStateContext.Provider value={value.dataReady}>
      <CustomPageDispatchActionContext.Provider value={value.dispatchAction}>
        <CustomPageProxiesClientContext.Provider value={value.proxiesClient}>
          <CustomPageSubjectContext.Provider value={value.subject}>
            <CustomPageUserContext.Provider value={value.user}>
              <CustomPageBrandContext.Provider value={value.brand}>
                <CustomPageTerritoryIdContext.Provider value={value.territoryId}>
                  <CustomPageArbitraryFilterFieldsContext.Provider value={value.arbitraryFilterFields}>
                    <CustomPageSetArbitraryFilterFieldsContext.Provider value={value.setArbitraryFilterFields}>
                      <FetchUserTerritoriesContext.Provider value={fetchUserTerritories}>
                        {children}
                      </FetchUserTerritoriesContext.Provider>
                    </CustomPageSetArbitraryFilterFieldsContext.Provider>
                  </CustomPageArbitraryFilterFieldsContext.Provider>
                </CustomPageTerritoryIdContext.Provider>
              </CustomPageBrandContext.Provider>
            </CustomPageUserContext.Provider>
          </CustomPageSubjectContext.Provider>
        </CustomPageProxiesClientContext.Provider>
      </CustomPageDispatchActionContext.Provider>
    </CustomPageDataReadyStateContext.Provider>
  );
}

type DummyCustomPageRendererContextProviderProps = {
  children: ReactNode;
};

const DUMMY_CUSTOM_PAGE_USER: CustomPageUser = {
  identifier: 'dummy-user',
  name: 'Preview User',
  email: 'preview.user@example.com',
  metadata: {},
  brandIndications: [],
};

const DUMMY_CUSTOM_PAGE_BRAND: CustomPageBrand = {
  identifier: 'dummy-brand',
  name: 'Preview Brand',
};

const DUMMY_CUSTOM_PAGE_SUBJECT: CustomPageSubject = {
  id: 'dummy-subject',
  name: 'Preview Subject',
  kind: 'demo',
};

export function DummyCustomPageRendererContextProvider({
  children,
}: DummyCustomPageRendererContextProviderProps): ReactElement {
  const [subject, setSubject] = useState<CustomPageSubject>(DUMMY_CUSTOM_PAGE_SUBJECT);
  const [arbitraryFilterFields, setArbitraryFilterFields] = useState<CustomPageArbitraryFilterFields>({});
  const [isDataReady, setDataReady] = useState(false);

  const markDataReady = useCallback(() => {
    setDataReady(true);
  }, []);

  const markDataLoading = useCallback(() => {
    setDataReady(false);
  }, []);

  const dispatchAction = useCallback<CustomPageDispatchAction>((action) => {
    switch (action.type) {
      case 'update-subject':
      case 'open-new-chat-for-subject':
        setSubject(action.payload.subject);
        break;
      case 'noop':
      case 'update-input':
      case 'submit-query':
        break;
      default:
        break;
    }
  }, []);

  return (
    <CustomPageRendererContextProvider
      value={{
        proxiesClient: null,
        subject,
        user: DUMMY_CUSTOM_PAGE_USER,
        brand: DUMMY_CUSTOM_PAGE_BRAND,
        territoryId: undefined,
        arbitraryFilterFields,
        setArbitraryFilterFields,
        dispatchAction,
        dataReady: {
          isDataReady,
          setDataReady,
          markDataReady,
          markDataLoading,
        },
      }}
      fetchUserTerritories={() => Promise.resolve([])}
    >
      {children}
    </CustomPageRendererContextProvider>
  );
}
